import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import MealPlanFields from 'components/meal-plan/MealPlanFields';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import defaultMealPlanFormValidations from 'common/validations/form/defaultMealPlan';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import { updateMealPlanInitialValues } from 'common/form/initial-values/update';
import getDataToSave from 'common/form/dataToSave';
import { getFieldsToUpdate } from 'common/form/utils';
import StyledButtonsContainer from 'common/theme/containers/StyledButtonsContainer';
import { handleClientIdChange } from 'common/utils/user';
import AppBarContext from 'context/AppBarContext';
import mealPlanSlice from 'store/modules/mealPlan';

const requiredFields = ['user_id', 'pdf_document', 'created_at'];

function MealPlanEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['routes']);
  const { id } = useParams();
  const pageTitle = t('mealPlan.edit', { ns: 'routes' });
  const [initialValues, setInitialValues] = useState({});
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const clientOptions = useSelector((state) => state.client.data.search);
  const mealPlan = useSelector((state) => state.mealPlan.data.currentMealPlan);
  const searchStatus = useSelector((state) => state.client.status.search);
  const status = useSelector((state) => state.mealPlan.status.general);
  const previousStatus = React.useRef(status);
  // `find` y `update` comparten el mismo status; sin este guard el find inicial rebotaría a la lista.
  const hasSubmitted = React.useRef(false);

  useEffect(() => {
    dispatch(mealPlanSlice.actions.find(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (mealPlan && Object.keys(mealPlan).length > 0 && mealPlan.id === id) {
      const mealPlanData = updateMealPlanInitialValues(mealPlan);
      setInitialValues(mealPlanData);
    }
  }, [mealPlan]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Navegar a la lista después de una edición exitosa
  useEffect(() => {
    if (
      hasSubmitted.current &&
      previousStatus.current === requestStatus.loading &&
      status === requestStatus.success
    ) {
      navigate(routes.listMealPlan.path);
    }
    previousStatus.current = status;
  }, [status, navigate]);

  const onClientIdChange = handleClientIdChange(dispatch);

  const onEdit = useCallback(
    (values) => {
      const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
      const dataToSave = getDataToSave(fieldsToUpdate, 'mealPlan');
      hasSubmitted.current = true;
      dispatch(mealPlanSlice.actions.update({ id, input: dataToSave }));
    },
    [dispatch, id, initialValues]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        onSubmit={onEdit}
        validate={defaultMealPlanFormValidations}
        render={({ handleSubmit, invalid, pristine }) => (
          <form onSubmit={handleSubmit}>
            <MealPlanFields
              clientOptions={clientOptions}
              onClientIdChange={onClientIdChange}
              requiredFields={requiredFields}
              searchStatus={searchStatus}
            />
            <StyledButtonsContainer>
              <SecondaryButtonLoading
                component={RouterLink}
                disabled={status === requestStatus.loading}
                style={{ marginRight: '32px' }}
                label={t('cancel', { ns: 'buttons' })}
                to={routes.listMealPlan.path}
                type="button"
              />
              <PrimaryLoadingButton
                disabled={invalid || pristine || status === requestStatus.loading}
                loading={status === requestStatus.loading}
                label={t('mealPlan.edit', { ns: 'buttons' })}
                type="submit"
              />
            </StyledButtonsContainer>
          </form>
        )}
      />
    </>
  );
}

export default MealPlanEdit;
